import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { gameCanvasSize } from "../../data/gameConstants";
import { Adjustment, SlideMenu, State } from "../menu/SlideMenu";
import { DialogueParser } from './DialogueParser';
import { ReputationIndicatorUI } from './ui components/ReputationIndicatorUI';
import { GameDebugInfoUI } from "./ui components/debug/GameDebugInfoUI";
import { InventoryUI } from "./ui components/InventoryUI";
import { QuestlogUI } from "./ui components/QuestlogUI";
import { GiSoundOn } from "react-icons/gi";
import { CgDebug } from "react-icons/cg";
import { BorderColor, BorderPrimary, FontColorHighlight, FontColorMain, FontColorModerate, GUITextBold } from "./ui components/GameUIElements";
import { UiConstants } from '../../data/UiConstants';
import { BsFillExclamationSquareFill } from "react-icons/bs";
import { MdAccessTimeFilled } from 'react-icons/md';
import { findDialogSpeaker, getTextOfAction } from '../../helper/gameActionTreeHelper';
import { SimpleCutSceneUI } from "./ui components/SimpleCutSceneUI";
import { AlignedContainer } from "./ui components/AlignedContainer";
import { combatStore } from '../../stores/CombatStore';
import { gameStore, MapLoadingState } from '../../stores/GameStore';
import { imageStore } from '../../stores/ImageStore';
import { localSettingsStore } from '../../stores/LocalSettingsStore';
import { MapLoadingScreen } from './ui components/MapLoadingScreen';
import { soundCache } from "../../stores/SoundCache";
import { UiSounds } from "../../canvas/game/sound/UiSounds";
import { NotificationContainer } from "./ui components/NotificationContainer";
import { notificationController } from "./ui components/NotificationController";
import { SoundDebugInfoUI } from "./ui components/debug/SoundDebugUI";
import { TaskMarkers } from './ui components/TaskMarkers';
import { PastNotifications } from './ui components/PastNotifications';
import { GameStatsContainer } from '../debug/GameStatsContainer';
import { DialogueAnswerWindow, DialogueSpeakerWindow, DialogueTextWindow, DialogueTextWindowWithoutSpeaker, TextActionWindow, TimerWindow } from './ui components/NineSlices';
const Container = styled.div.withConfig({
  displayName: "GameUI__Container",
  componentId: "sc-bfj9fe-0"
})(["user-select:none;"]);
const BlockingContainer = styled.div.withConfig({
  displayName: "GameUI__BlockingContainer",
  componentId: "sc-bfj9fe-1"
})(["position:absolute;left:0;right:0;top:30px;bottom:0;padding-top:40px;max-width:100%;width:", "px;box-sizing:border-box;user-select:none;"], gameCanvasSize.width);
const ExclamationIcon = styled(BsFillExclamationSquareFill).withConfig({
  displayName: "GameUI__ExclamationIcon",
  componentId: "sc-bfj9fe-2"
})(["margin-right:4px;"]);
const DialogArea = styled.div.withConfig({
  displayName: "GameUI__DialogArea",
  componentId: "sc-bfj9fe-3"
})(["position:absolute;left:0;right:0;bottom:40px;margin-left:auto;margin-right:auto;width:35%;z-index:", ";"], UiConstants.Z_INDEX_DIALOG);
const DialogueBoxContainer = styled.div.withConfig({
  displayName: "GameUI__DialogueBoxContainer",
  componentId: "sc-bfj9fe-4"
})(["position:relative;pointer-events:none;", " margin-bottom:-3px;"], props => props.clickable && css(["*{cursor:pointer;}"]));
const DialogueSpeaker = styled(DialogueSpeakerWindow).withConfig({
  displayName: "GameUI__DialogueSpeaker",
  componentId: "sc-bfj9fe-5"
})(["pointer-events:all;width:fit-content;font-size:larger;color:#CFD4DE;margin-bottom:-35px;"]);
const DialogueText = styled(DialogueTextWindow).withConfig({
  displayName: "GameUI__DialogueText",
  componentId: "sc-bfj9fe-6"
})(["pointer-events:all;width:100%;color:", ";"], FontColorMain);
const DialogueTextWithoutSpeaker = styled(DialogueTextWindowWithoutSpeaker).withConfig({
  displayName: "GameUI__DialogueTextWithoutSpeaker",
  componentId: "sc-bfj9fe-7"
})(["pointer-events:all;width:100%;color:", ";"], FontColorMain);
export const DialogueCloseButton = styled.img.withConfig({
  displayName: "GameUI__DialogueCloseButton",
  componentId: "sc-bfj9fe-8"
})(["position:absolute;right:18px;bottom:10px;"]);
const TimerValueContainer = styled.div.withConfig({
  displayName: "GameUI__TimerValueContainer",
  componentId: "sc-bfj9fe-9"
})(["display:flex;flex-direction:row;align-items:center;justify-content:center;"]);
export const Headline = styled.div.withConfig({
  displayName: "GameUI__Headline",
  componentId: "sc-bfj9fe-10"
})(["display:flex;flex-direction:row;flex-grow:1;"]);
const TimerBoxContainer = styled.div.withConfig({
  displayName: "GameUI__TimerBoxContainer",
  componentId: "sc-bfj9fe-11"
})(["position:absolute;top:80px;left:0px;right:0px;display:flex;align-items:center;justify-content:center;flex-direction:row;pointer-events:none;"]);
const TimerIcon = styled(MdAccessTimeFilled).withConfig({
  displayName: "GameUI__TimerIcon",
  componentId: "sc-bfj9fe-12"
})(["color:", ";margin-right:4px;"], FontColorHighlight);
const TextBox = styled(TextActionWindow).withConfig({
  displayName: "GameUI__TextBox",
  componentId: "sc-bfj9fe-13"
})(["position:absolute;padding:8px;max-height:200px;min-width:100px;max-width:400px;left:-4px;bottom:91px;display:flex;align-items:center;justify-content:center;*{color:#CFD4DE;}"]);
const ImageBox = styled.img.withConfig({
  displayName: "GameUI__ImageBox",
  componentId: "sc-bfj9fe-14"
})(["", " background-color:white;padding:4px;margin:10%;max-width:80%;height:70%;"], BorderPrimary);
const ChoiceButtonArea = styled.div.withConfig({
  displayName: "GameUI__ChoiceButtonArea",
  componentId: "sc-bfj9fe-15"
})(["padding:0 35px;"]);
const ChoiceButton = styled.button.withConfig({
  displayName: "GameUI__ChoiceButton",
  componentId: "sc-bfj9fe-16"
})(["border:", ";background-color:transparent;margin:-8px 0;padding:0;border:0;width:100%;color:", ";cursor:pointer;"], props => props.isSelected ? "5px solid orange" : "", FontColorModerate);
export const GameUI = observer(() => {
  const {
    t
  } = useTranslation();
  const {
    accessibilityOptions,
    gameEngine,
    languageKey
  } = gameStore;

  if (gameStore.mapLoadingState === MapLoadingState.LoadingMapFromServer) {
    return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(MapLoadingScreen, null));
  }

  if (!gameEngine || combatStore.active) return null;
  const {
    rootActionTree
  } = gameEngine;
  const {
    currentDialogueSelection,
    activeText,
    activeImage,
    activeTimers,
    visibleRunningTimer
  } = gameEngine.gameState;
  const activeDialogOrDescription = gameEngine.activeDialogueOrDescription();
  const activeNonBlockingDialogs = gameEngine.activeNonBlockingDialogues();
  const activeTextAction = activeText ? gameEngine.getCachedActionNode(activeText) : null;
  const activeImageAction = activeImage ? gameEngine.getCachedActionNode(activeImage) : null;
  const activeTimerAction = visibleRunningTimer ? gameEngine.getCachedActionNode(visibleRunningTimer) : null;
  const currentCutScene = gameEngine.gameState.actionPropertyCurrentCutScene;
  const uiVisible = gameEngine.gameState.actionPropertyUIVisible && !gameStore.isLoadingMap;

  function onClickNonBlockingDialogue(modelId) {
    gameStore.gameEngine.removeNonBlockingDialog(modelId);
  }

  const activeTextValue = activeTextAction ? getTextOfAction(activeTextAction, null, t) : null;
  return /*#__PURE__*/React.createElement(Container, null, notificationController.visibleNotifications.map(ni => /*#__PURE__*/React.createElement(NotificationContainer, {
    key: ni.id,
    info: ni
  })), uiVisible && /*#__PURE__*/React.createElement(SlideMenu, {
    identifier: "game-inventory",
    orientation: Adjustment.Left,
    start: 80,
    icon: /*#__PURE__*/React.createElement("img", {
      src: "assets/game/images/UI/icon_inventory.png"
    }),
    state: State.Expanded,
    collapsible: true,
    transparent: true,
    closeButtonOffsetX: 6,
    closeButtonOffsetY: 7,
    menuOverflow: "unset",
    onUserToggle: state => {
      if (state == State.Expanded) soundCache.playOneOf(UiSounds.OPEN_INVENTORY);
      if (state == State.Collapsed) soundCache.playOneOf(UiSounds.CLOSE_INVENTORY);
    }
  }, /*#__PURE__*/React.createElement(InventoryUI, null)), uiVisible && /*#__PURE__*/React.createElement(SlideMenu, {
    orientation: Adjustment.Right,
    start: 80,
    state: State.Collapsed,
    collapsible: true,
    identifier: "game-quests",
    icon: /*#__PURE__*/React.createElement("img", {
      src: "assets/game/images/UI/icon_quests.png"
    }),
    transparent: true,
    closeButtonOffsetX: 6,
    closeButtonOffsetY: 7,
    menuOverflow: "unset"
  }, /*#__PURE__*/React.createElement(QuestlogUI, null)), uiVisible && /*#__PURE__*/React.createElement(SlideMenu, {
    identifier: "past-notifications",
    orientation: Adjustment.Bottom,
    start: 1280 - 310,
    width: 310,
    icon: /*#__PURE__*/React.createElement("img", {
      src: "assets/game/images/UI/icon_notifications.png"
    }),
    iconStart: 1280 - UiConstants.SIZE_SLIDE_MENU_ICON_NUMBER,
    state: State.Collapsed,
    collapsible: true,
    notAnimated: true,
    transparent: true,
    closeButtonOffsetX: 6,
    closeButtonOffsetY: 7,
    menuOverflow: "unset"
  }, /*#__PURE__*/React.createElement(PastNotifications, null)), localSettingsStore.showDebugInfo && /*#__PURE__*/React.createElement(SlideMenu, {
    orientation: Adjustment.Bottom,
    start: 0,
    state: State.Collapsed,
    collapsible: true,
    identifier: "game-debug-info",
    icon: /*#__PURE__*/React.createElement(CgDebug, null),
    menuBackgroundColor: BorderColor,
    width: "100%"
  }, /*#__PURE__*/React.createElement(GameDebugInfoUI, null)), localSettingsStore.showDebugInfo && /*#__PURE__*/React.createElement(SlideMenu, {
    identifier: "sound-debug-info",
    orientation: Adjustment.Left,
    start: 150,
    icon: /*#__PURE__*/React.createElement(GiSoundOn, null),
    state: State.Expanded,
    collapsible: true,
    menuBackgroundColor: BorderColor
  }, /*#__PURE__*/React.createElement(SoundDebugInfoUI, null)), (uiVisible && activeDialogOrDescription || activeNonBlockingDialogs.length > 0) && /*#__PURE__*/React.createElement(DialogArea, null, activeNonBlockingDialogs.map(action => /*#__PURE__*/React.createElement(DialogueBoxContainer, {
    key: action.$modelId,
    onClick: _ => onClickNonBlockingDialogue(action.$modelId),
    clickable: true
  }, findDialogSpeaker(action, rootActionTree, t) !== "" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DialogueSpeaker, null, /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: findDialogSpeaker(action, rootActionTree, t),
    initialTreeScopeContext: action
  })), /*#__PURE__*/React.createElement(DialogueText, null, /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: getTextOfAction(action, null, t),
    initialTreeScopeContext: action
  }), /*#__PURE__*/React.createElement("div", null))) : /*#__PURE__*/React.createElement(DialogueTextWithoutSpeaker, null, /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: getTextOfAction(action, null, t),
    initialTreeScopeContext: action
  }), /*#__PURE__*/React.createElement("div", null)), /*#__PURE__*/React.createElement(DialogueCloseButton, {
    src: "assets/game/images/UI/continue_arrow.png"
  }))), activeDialogOrDescription && /*#__PURE__*/React.createElement(DialogueBoxContainer, {
    clickable: false
  }, findDialogSpeaker(activeDialogOrDescription, rootActionTree, t) !== "" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DialogueSpeaker, null, /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: findDialogSpeaker(activeDialogOrDescription, rootActionTree, t),
    initialTreeScopeContext: activeDialogOrDescription
  })), /*#__PURE__*/React.createElement(DialogueText, null, /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: getTextOfAction(activeDialogOrDescription, null, t),
    initialTreeScopeContext: activeDialogOrDescription
  }))) : /*#__PURE__*/React.createElement(DialogueTextWithoutSpeaker, null, /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: getTextOfAction(activeDialogOrDescription, null, t),
    initialTreeScopeContext: activeDialogOrDescription
  }))), /*#__PURE__*/React.createElement(ChoiceButtonArea, null, gameEngine.availableDialogueAnswers().map((exit, index) => /*#__PURE__*/React.createElement(ChoiceButton, {
    isSelected: accessibilityOptions && currentDialogueSelection === index,
    onClick: () => gameEngine.selectDialogueAnswer(index),
    key: exit.$modelId
  }, /*#__PURE__*/React.createElement(DialogueAnswerWindow, null, /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: exit.value.getForGender(gameStore.languageKey, gameStore.playerGender, true),
    initialTreeScopeContext: activeDialogOrDescription
  })))))), uiVisible && (activeTimerAction === null || activeTimerAction === void 0 ? void 0 : activeTimerAction.visible) && /*#__PURE__*/React.createElement(TimerBoxContainer, null, /*#__PURE__*/React.createElement(TimerWindow, null, /*#__PURE__*/React.createElement(TimerIcon, null), /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: activeTimerAction.text.get(languageKey),
    initialTreeScopeContext: activeTimerAction
  }), /*#__PURE__*/React.createElement(TimerValueContainer, null, /*#__PURE__*/React.createElement(GUITextBold, null, activeTimerAction.countDown ? activeTimers.get(visibleRunningTimer) : parseInt(gameEngine.resolvePotentialTreeParameter(activeTimerAction.time, activeTimerAction)) - activeTimers.get(visibleRunningTimer))))), uiVisible && activeTextValue && /*#__PURE__*/React.createElement(TextBox, null, /*#__PURE__*/React.createElement(GUITextBold, null, /*#__PURE__*/React.createElement(ExclamationIcon, null), /*#__PURE__*/React.createElement(DialogueParser, {
    dialogueString: activeTextValue,
    initialTreeScopeContext: activeTextAction
  }))), uiVisible && activeImageAction && /*#__PURE__*/React.createElement(BlockingContainer, {
    onClick: () => gameEngine.closeActiveImage()
  }, /*#__PURE__*/React.createElement(ImageBox, {
    src: imageStore.getImageUrl(activeImageAction.imageId)
  })), uiVisible && /*#__PURE__*/React.createElement(ReputationIndicatorUI, null), uiVisible && /*#__PURE__*/React.createElement(TaskMarkers, null), currentCutScene && currentCutScene.text && currentCutScene.text.length && /*#__PURE__*/React.createElement(AlignedContainer, {
    direction: currentCutScene.textContainerAlignmentDirection[gameEngine.gameState.actionPropertyCurrentCutSceneTextIndex]
  }, /*#__PURE__*/React.createElement(SimpleCutSceneUI, {
    sceneProps: currentCutScene
  })), /*#__PURE__*/React.createElement(GameStatsContainer, null));
});